// Derslerim, ders ayrıntısı, öğrencilerim ve profil.
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { LuArrowLeft, LuBadgeCheck, LuCopy, LuDownload, LuPlay, LuSearch, LuTrash2 } from 'react-icons/lu';
import { PLANS, PLAN_IDS, STATUS_LABELS, planOf, type LessonSummary, type PlanChange, type PlanId, type PlanRequest, type TeacherReport } from '../../shared/plans';
import { LIMITS } from '../../shared/protocol';
import { AppShell, type Me } from '../components/AppShell';
import { CenterCard } from '../components/ui';
import { api } from '../lib/api';
import { studentLink, teacherLink } from '../lib/storage';
import { copyText, formatDate, formatDuration, formatWhen } from '../lib/util';
import { ChangePasswordForm } from './Dashboard';

const RECORD_NOTE = 'Ders süreleri, bu özellik eklendikten sonra yapılan derslerden itibaren ölçülür.';

function useReport() {
  const [report, setReport] = useState<TeacherReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(() => {
    api<TeacherReport>('/api/teacher/report').then(setReport, (err: Error) => setError(err.message));
  }, []);
  useEffect(load, [load]);
  return { report, error, load };
}

const exportUrl = (l: LessonSummary) => `/api/lessons/${l.id}/export?key=${encodeURIComponent(l.teacherKey)}`;

function CopyLink({ id }: { id: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="btn btn-sm"
      onClick={async () => {
        if (await copyText(studentLink(id))) {
          setCopied(true);
          setTimeout(() => setCopied(false), 1600);
        }
      }}
    >
      <LuCopy /> {copied ? 'Kopyalandı' : 'Öğrenci bağlantısı'}
    </button>
  );
}

export function LessonsPage({ me, onChange }: { me: Me; onChange: () => void }) {
  const { report, error, load } = useReport();
  const [actionError, setActionError] = useState<string | null>(null);
  const plan = planOf(me.account.plan);

  const remove = async (l: LessonSummary) => {
    if (!window.confirm(`"${l.title}" dersi, çizimleri, süre kayıtları ve PDF'iyle birlikte kalıcı olarak silinsin mi?`)) return;
    setActionError(null);
    try {
      await api(`/api/teacher/lessons/${l.id}`, { method: 'DELETE' });
      load();
      onChange();
    } catch (err) {
      setActionError((err as Error).message);
    }
  };

  return (
    <AppShell me={me} page="dersler" title="Derslerim" subtitle={`Dersler, son etkinlikten ${plan.retentionDays} gün sonra otomatik silinir. Önemli notlarınızı PDF olarak indirin.`}>
      {(error || actionError) && <p className="form-error">{error ?? actionError}</p>}
      {!report && !error && <p className="muted">Yükleniyor…</p>}
      {report?.lessons.length === 0 && (
        <div className="card empty">
          <p className="muted">Henüz ders yok.</p>
          <a className="btn btn-primary" href="/">
            İlk dersi başlat
          </a>
        </div>
      )}
      {report && report.lessons.length > 0 && (
        <div className="card table-card">
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Ders</th>
                  <th>Son etkinlik</th>
                  <th className="num">Süre</th>
                  <th className="num">Öğrenci</th>
                  <th className="num">Çizim</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {report.lessons.map((l) => (
                  <tr key={l.id}>
                    <td>
                      <a className="row-title" href={`/derslerim/${l.id}`}>
                        {l.title}
                      </a>
                      <span className="muted small row-sub">
                        {l.id} · {l.pageCount ? `${l.pageCount} sayfa` : l.pdfName}
                      </span>
                    </td>
                    <td className="nowrap">{formatWhen(l.lastActivityMs)}</td>
                    <td className="num nowrap">{formatDuration(l.durationMs)}</td>
                    <td className="num">{l.studentCount}</td>
                    <td className="num">{l.annotationCount}</td>
                    <td className="row-actions">
                      <a className="btn btn-sm" href={teacherLink(l.id, l.teacherKey)}>
                        <LuPlay /> Derse gir
                      </a>
                      <a className="btn btn-sm" href={exportUrl(l)} aria-label={`${l.title} notlarını PDF olarak indir`}>
                        <LuDownload /> Notlar
                      </a>
                      <button type="button" className="icon-btn is-danger" aria-label={`${l.title} dersini sil`} title="Dersi sil" onClick={() => void remove(l)}>
                        <LuTrash2 />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <p className="muted small page-note">{RECORD_NOTE}</p>
    </AppShell>
  );
}

export function LessonDetailPage({ me, id }: { me: Me; id: string }) {
  const { report, error } = useReport();
  const lesson = report?.lessons.find((l) => l.id === id);

  if (error) return <CenterCard title="Ders yüklenemedi" text={error} />;
  if (report && !lesson) {
    return (
      <CenterCard title="Ders bulunamadı" text="Bu ders silinmiş ya da başka bir hesaba ait olabilir.">
        <a className="btn btn-primary" href="/derslerim">
          Derslerime dön
        </a>
      </CenterCard>
    );
  }
  if (!lesson) return <div className="center-screen" aria-busy="true" />;

  const people = lesson.participants;
  return (
    <AppShell
      me={me}
      page="dersler"
      title={lesson.title}
      subtitle={`Oluşturulma: ${formatWhen(lesson.createdAt)} · ${lesson.pdfName}`}
      actions={
        <>
          <CopyLink id={lesson.id} />
          <a className="btn btn-sm" href={exportUrl(lesson)}>
            <LuDownload /> Notları PDF indir
          </a>
          <a className="btn btn-sm btn-primary" href={teacherLink(lesson.id, lesson.teacherKey)}>
            <LuPlay /> Derse gir
          </a>
        </>
      }
    >
      <a className="back-link" href="/derslerim">
        <LuArrowLeft /> Derslerim
      </a>
      <div className="stats">
        <div className="stat">
          <span className="stat-label">Ders süresi</span>
          <strong className="stat-value">{formatDuration(lesson.durationMs)}</strong>
        </div>
        <div className="stat">
          <span className="stat-label">Öğrenci</span>
          <strong className="stat-value">{lesson.studentCount}</strong>
        </div>
        <div className="stat">
          <span className="stat-label">Çizim ve not</span>
          <strong className="stat-value">{lesson.annotationCount}</strong>
        </div>
      </div>
      <section className="card table-card">
        <h2 className="table-title">Katılımcılar</h2>
        {people.length === 0 ? (
          <p className="muted small table-empty">Bu derse henüz kimse katılmadı.</p>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Ad</th>
                  <th>Rol</th>
                  <th className="num">Süre</th>
                  <th>Son görülme</th>
                </tr>
              </thead>
              <tbody>
                {people.map((p) => (
                  <tr key={`${p.role}:${p.name}`}>
                    <td className="row-title-plain">{p.name}</td>
                    <td>{p.role === 'teacher' ? 'Öğretmen' : 'Öğrenci'}</td>
                    <td className="num nowrap">{formatDuration(p.ms)}</td>
                    <td className="nowrap">{p.lastSeenMs ? formatWhen(p.lastSeenMs) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <p className="muted small page-note">Ders notları, derste yapılan çizim ve yazılardır; PDF üzerine işlenmiş olarak inerler. Ses veya görüntü kaydı tutulmaz. {RECORD_NOTE}</p>
    </AppShell>
  );
}

export function StudentsPage({ me }: { me: Me }) {
  const { report, error } = useReport();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const rows = useMemo(() => {
    const q = query.trim().toLocaleLowerCase('tr');
    return (report?.students ?? []).filter((s) => !q || s.name.toLocaleLowerCase('tr').includes(q));
  }, [report, query]);

  return (
    <AppShell me={me} page="ogrenciler" title="Öğrencilerim" subtitle="Hangi öğrenciyle ne kadar çalıştığınızı görün.">
      {error && <p className="form-error">{error}</p>}
      {!report && !error && <p className="muted">Yükleniyor…</p>}
      {report?.students.length === 0 && (
        <div className="card empty">
          <p className="muted">Derslerinize öğrenci katıldığında burada görünür.</p>
        </div>
      )}
      {report && report.students.length > 0 && (
        <>
          <label className="search-box">
            <LuSearch />
            <input id="student-search" type="search" placeholder="Öğrenci ara" aria-label="Öğrenci ara" value={query} onChange={(e) => setQuery(e.target.value)} />
          </label>
          <div className="card table-card">
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Öğrenci</th>
                    <th className="num">Ders</th>
                    <th className="num">Birlikte çalışılan süre</th>
                    <th>Son ders</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((s) => (
                    <Fragment key={s.name}>
                      <tr>
                        <td className="row-title-plain">{s.name}</td>
                        <td className="num">{s.lessonCount}</td>
                        <td className="num nowrap">{formatDuration(s.totalMs)}</td>
                        <td className="nowrap">{s.lastSeenMs ? formatWhen(s.lastSeenMs) : '—'}</td>
                        <td className="row-actions">
                          <button type="button" className="btn btn-sm" aria-expanded={open === s.name} onClick={() => setOpen(open === s.name ? null : s.name)}>
                            {open === s.name ? 'Gizle' : 'Dersler'}
                          </button>
                        </td>
                      </tr>
                      {open === s.name && (
                        <tr className="row-detail">
                          <td colSpan={5}>
                            <ul>
                              {s.lessons.map((l) => (
                                <li key={l.id}>
                                  <a href={`/derslerim/${l.id}`}>{l.title}</a>
                                  <span className="muted small">
                                    {formatWhen(l.at)} · {formatDuration(l.ms)}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
      <p className="muted small page-note">Süre, öğrenci ile sizin derste birlikte bulunduğunuz zamandır. Öğrencilerin hesabı olmadığı için eşleştirme derste yazdıkları ada göre yapılır; aynı adlı iki öğrenci tek satırda görünür, farklı yazılan ad ise ayrı satırda. Silinen derslerin süreleri de silinir. {RECORD_NOTE}</p>
    </AppShell>
  );
}

function NameForm({ me, onChange }: { me: Me; onChange: () => void }) {
  const [name, setName] = useState(me.user.name);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setNote(null);
    setError(null);
    try {
      await api('/api/account/profile', { method: 'PATCH', body: { name } });
      setNote('Adınız güncellendi.');
      onChange();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit}>
      <label className="field">
        <span>Ad Soyad</span>
        <input id="profile-name" required maxLength={LIMITS.maxNameLength} value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      {note && <p className="form-note-ok">{note}</p>}
      {error && <p className="form-error">{error}</p>}
      <button type="submit" className="btn" disabled={busy || name.trim() === me.user.name}>
        Adı kaydet
      </button>
    </form>
  );
}

function PlanRequestDialog({ me, plan, onClose, onSent }: { me: Me; plan: PlanId; onClose: () => void; onSent: () => void }) {
  const [phone, setPhone] = useState('');
  const [institution, setInstitution] = useState('');
  const [students, setStudents] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const label = PLANS[plan].label;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const count = students.trim() ? Number(students) : null;
    if (count !== null && !Number.isInteger(count)) return setError('Öğrenci sayısını rakamla yazın.');
    setBusy(true);
    setError(null);
    try {
      await api('/api/account/plan-request', { method: 'POST', body: { plan, phone, institution, studentCount: count, message } });
      onSent();
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  return (
    <div className="math-backdrop" onPointerDown={(e) => e.target === e.currentTarget && onClose()}>
      <form className="card math-dialog" role="dialog" aria-label={`${label} paketi için fiyat teklifi`} onSubmit={submit} onKeyDown={(e) => e.key === 'Escape' && onClose()}>
        <h2>{label} paketi için özel fiyat</h2>
        <p className="muted small request-intro">
          Size özel fiyat için bilgilerinizi bırakın, sizinle iletişime geçelim. Yanıtı <strong>{me.user.email}</strong> adresine göndereceğiz.
        </p>
        <label className="field">
          <span>Telefon (isteğe bağlı)</span>
          <input id="request-phone" type="tel" autoComplete="tel" autoFocus maxLength={25} value={phone} onChange={(e) => setPhone(e.target.value)} />
        </label>
        <label className="field">
          <span>Kurum (isteğe bağlı)</span>
          <input id="request-institution" autoComplete="organization" maxLength={120} value={institution} onChange={(e) => setInstitution(e.target.value)} />
        </label>
        <label className="field">
          <span>Yaklaşık öğrenci sayısı (isteğe bağlı)</span>
          <input id="request-students" inputMode="numeric" maxLength={6} value={students} onChange={(e) => setStudents(e.target.value.replace(/[^0-9]/g, ''))} />
        </label>
        <label className="field">
          <span>Mesajınız (isteğe bağlı)</span>
          <textarea id="request-message" className="request-message" rows={3} maxLength={1000} value={message} onChange={(e) => setMessage(e.target.value)} />
        </label>
        {error && <p className="form-error">{error}</p>}
        <div className="math-actions">
          <button type="button" className="btn" onClick={onClose}>
            Vazgeç
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Gönderiliyor…' : 'Talebi gönder'}
          </button>
        </div>
      </form>
    </div>
  );
}

function PlanSection({ me, onChange }: { me: Me; onChange: () => void }) {
  const [request, setRequest] = useState<PlanRequest | null>(null);
  const [asking, setAsking] = useState<PlanId | null>(null);
  const [history, setHistory] = useState<PlanChange[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const current = me.account.plan;
  const currentRank = PLAN_IDS.indexOf(current);

  const loadHistory = useCallback(() => {
    api<{ changes: PlanChange[] }>('/api/account/plan-changes').then(
      (r) => setHistory(r.changes),
      () => setHistory([]),
    );
  }, []);
  useEffect(loadHistory, [loadHistory]);
  const loadRequest = useCallback(() => {
    api<{ request: PlanRequest | null }>('/api/account/plan-request').then(
      (r) => setRequest(r.request),
      () => setRequest(null),
    );
  }, []);
  useEffect(loadRequest, [loadRequest]);

  const change = async (plan: PlanId) => {
    const target = PLANS[plan];
    const warning = `${target.label} paketine geçilsin mi?\n\nDers saklama süresi ${target.retentionDays} güne iner: son etkinliği bundan eski dersler ilk temizlikte silinir. Önemli notlarınızı önce PDF olarak indirin. Yeniden yükseltmek için fiyat teklifi istemeniz gerekir.`;
    if (!window.confirm(warning)) return;
    setBusy(true);
    setError(null);
    try {
      await api('/api/account/plan', { method: 'POST', body: { plan } });
      onChange();
      loadHistory();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card profile-section" id="paket">
      <div className="section-head">
        <h2>Paket</h2>
        <span className={`plan-status ${me.account.entitled ? 'is-ok' : 'is-off'}`}>{STATUS_LABELS[me.account.status]}</span>
      </div>
      <p className="muted small">
        {me.account.lessonCount} ders kullanıyorsunuz.
        {me.account.periodEndMs !== null && ` Bitiş tarihi: ${formatDate(me.account.periodEndMs)}.`} Üst paketlerin fiyatı ihtiyacınıza göre belirlenir.
      </p>
      {error && <p className="form-error">{error}</p>}
      <div className="plans">
        {PLAN_IDS.map((id, rank) => {
          const p = PLANS[id];
          const isCurrent = id === current;
          return (
            <article key={id} className={`plan ${isCurrent ? 'is-current' : ''}`}>
              <h3>{p.label}</h3>
              <ul>
                <li>{p.maxLessons} ders</li>
                <li>Derste {p.maxStudents} öğrenci</li>
                <li>{p.maxPdfMb} MB’a kadar PDF</li>
                <li>Dersler {p.retentionDays} gün saklanır</li>
              </ul>
              {isCurrent ? (
                <span className="plan-current">
                  <LuBadgeCheck /> Mevcut paketiniz
                </span>
              ) : rank > currentRank ? (
                request?.plan === id ? (
                  <span className="plan-pending">Talebiniz alındı. Size dönüş yapacağız.</span>
                ) : (
                  <button type="button" className="btn btn-primary" disabled={!me.account.entitled} onClick={() => setAsking(id)}>
                    Özel fiyat teklifi al
                  </button>
                )
              ) : (
                <button type="button" className="btn" disabled={busy || !me.account.entitled} onClick={() => void change(id)}>
                  Paketi düşür
                </button>
              )}
            </article>
          );
        })}
      </div>
      {asking && (
        <PlanRequestDialog
          me={me}
          plan={asking}
          onClose={() => setAsking(null)}
          onSent={() => {
            setAsking(null);
            loadRequest();
          }}
        />
      )}
      {history && history.length > 0 && (
        <>
          <h3 className="history-title">Paket geçmişi</h3>
          <ul className="history">
            {history.map((c) => (
              <li key={c.at}>
                <span>
                  {planOf(c.from).label} → {planOf(c.to).label}
                </span>
                <span className="muted small">{formatWhen(c.at)}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

export function ProfilePage({ me, onChange }: { me: Me; onChange: () => void }) {
  const [saved, setSaved] = useState(false);
  return (
    <AppShell me={me} page="profil" title="Profil" subtitle="Hesap bilgileriniz, şifreniz ve paketiniz.">
      <div className="profile-grid">
        <section className="card profile-section">
          <h2>Hesap</h2>
          <NameForm me={me} onChange={onChange} />
          <dl className="profile-facts">
            <dt>E-posta</dt>
            <dd>{me.user.email}</dd>
            <dt>E-posta doğrulaması</dt>
            <dd>
              <span className="plan-status is-ok">Doğrulandı</span>
              {me.user.emailVerifiedMs ? <span className="muted small"> {formatDate(me.user.emailVerifiedMs)}</span> : null}
            </dd>
            <dt>Üyelik tarihi</dt>
            <dd>{formatDate(me.user.createdAt)}</dd>
          </dl>
        </section>
        <section className="card profile-section">
          <h2>Şifre değiştir</h2>
          <ChangePasswordForm onDone={() => setSaved(true)} />
          {saved && <p className="form-note-ok profile-saved">Şifreniz değiştirildi. Diğer cihazlardaki oturumlar kapatıldı.</p>}
        </section>
      </div>
      <PlanSection me={me} onChange={onChange} />
    </AppShell>
  );
}
